import { dayKeyInZone } from '@/lib/dayKey';
import type { StreamLinkRow, StreamPlatform } from '@/models/eventCourtStreamLinkModel';
import { recogniseStreamUrl, type StreamDisplay } from './streamLinks';

/**
 * Event court feeds — the pure half of the one reader every surface (Watch tab, match page,
 * admin, app) uses. A feed is either an app stream (`source: 'app'`, one per stream session,
 * built by `appCourtFeeds`) or a pasted link (`source: 'link'`, `event_court_stream_link`).
 * Days are `YYYY-MM-DD` keys on the event's calendar (its zone), never the viewer's.
 * No IO here: `eventFeedsLoad.ts` reads and gates.
 */
export type FeedStatus = 'live' | 'soon' | 'camera' | 'replay' | 'later';
export type CourtFeed = {
  /** The link id, or `app:<sessionId>`. */
  id: string;
  source: 'app' | 'link';
  courtId: string;
  /** App: `[its day]`; link: the stored days (`null` = every day). */
  days: string[] | null;
  platform: StreamPlatform;
  url: string;
  embedUrl: string | null;
  display: StreamDisplay;
  cameraName: string | null;
  /** App feeds: -1 (always first). */
  position: number;
  appState: 'live' | 'replay' | null;
  /** An app feed's other destinations. */
  alsoOn: { platform: StreamPlatform; url: string }[];
  /** App feeds: the primary destination's start (the video's t=0), when known. */
  startIso?: string | null;
};
export type CourtScheduleSlot = {
  scheduleId: string;
  courtId: string;
  startIso: string;
  durationMin: number | null;
  inProgress: boolean;
  /** The schedule row is completed (a result is in). */
  completed: boolean;
};

/** Most days an event spans for feed purposes. */
export const MAX_EVENT_DAYS = 31;
/** Assumed match length when the schedule has none. */
const DEFAULT_DURATION_MIN = 45;
/** A link becomes `soon` this long before the court's first match of the day. */
const SOON_MS = 24 * 60 * 60_000;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** A bare date is already a day key; an instant is read on the event's calendar. */
const dayKeyOf = (value: string, timeZone: string) =>
  DATE_ONLY.test(value) ? value : dayKeyInZone(value, timeZone);
const keyMs = (key: string) => Date.parse(`${key}T00:00:00Z`);

/** The event's day keys, start..end inclusive in `timeZone`, at most 31. An end before the start is one day. */
export function eventDayKeys(startIso: string, endIso: string, timeZone: string): string[] {
  const start = dayKeyOf(startIso, timeZone);
  const startMs = keyMs(start);
  if (Number.isNaN(startMs)) return [];
  const endMs = keyMs(dayKeyOf(endIso, timeZone));
  const keys: string[] = [];
  for (
    let ms = startMs;
    keys.length < MAX_EVENT_DAYS && (keys.length === 0 || ms <= endMs);
    ms += 86_400_000
  ) {
    keys.push(new Date(ms).toISOString().slice(0, 10));
  }
  return keys;
}

/** The day a Watch view shows: today when it is an event day (or there are none), else the nearest end of the event. */
export function shownDay(days: string[], todayKey: string): string {
  if (days.includes(todayKey) || days.length === 0) return todayKey;
  return todayKey < days[0]! ? days[0]! : days[days.length - 1]!;
}

/** The Watch day: `?day=` (first value of an array) when it is an event day, else `shownDay`. */
export function resolveWatchDay(
  asked: string | string[] | undefined,
  days: string[],
  todayKey: string,
): string {
  const first = Array.isArray(asked) ? asked[0] : asked;
  return first && days.includes(first) ? first : shownDay(days, todayKey);
}

const onDay = (feed: CourtFeed, dayKey: string) => feed.days === null || feed.days.includes(dayKey);
const rank = (feed: CourtFeed) => (feed.source === 'app' ? (feed.appState === 'live' ? 0 : 2) : 1);

/** One court's feeds on one day: live app streams, then links by position, then app replays. */
export function feedsForCourtDay(feeds: CourtFeed[], courtId: string, dayKey: string): CourtFeed[] {
  return feeds
    .filter((feed) => feed.courtId === courtId && onDay(feed, dayKey))
    .sort((a, b) => rank(a) - rank(b) || a.position - b.position || a.id.localeCompare(b.id));
}

/** One court's slots on one event day, in start order. */
function courtDaySlots(
  slots: CourtScheduleSlot[],
  courtId: string,
  dayKey: string,
  timeZone: string,
) {
  return slots
    .filter((s) => s.courtId === courtId && dayKeyInZone(s.startIso, timeZone) === dayKey)
    .map((s) => ({ slot: s, start: Date.parse(s.startIso) }))
    .filter((s) => Number.isFinite(s.start))
    .sort((a, b) => a.start - b.start);
}

/**
 * What a feed shows on `dayKey`. App feeds read their own state. A link before today is a
 * replay; on today or later, with no slots on its court that day it is a plain camera; it is
 * live while a slot is in progress or `now` is between the first start and the last start plus
 * its duration (45 min by default), `soon` from 24 h before the first start, a replay once the
 * day's last match is over, and `later` on a future day further out.
 */
export function feedStatus(
  feed: CourtFeed,
  dayKey: string,
  todayKey: string,
  slots: CourtScheduleSlot[],
  now: number,
  timeZone: string,
): FeedStatus {
  if (feed.source === 'app' && feed.appState) return feed.appState;
  if (dayKey < todayKey) return 'replay';
  const day = courtDaySlots(slots, feed.courtId, dayKey, timeZone);
  if (!day.length) return dayKey > todayKey ? 'later' : 'camera';
  if (dayKey === todayKey && day.some((s) => s.slot.inProgress)) return 'live';
  const first = day[0].start;
  const end = Math.max(
    ...day.map((s) => s.start + (s.slot.durationMin ?? DEFAULT_DURATION_MIN) * 60_000),
  );
  if (now >= first && now <= end) return 'live';
  if (now < first) return now >= first - SOON_MS ? 'soon' : 'later';
  return 'replay';
}

/**
 * The court's match to show beside its feed: the one in progress; else the earliest not-completed
 * match scheduled after the court's last completed (or in-progress) match, i.e. its queue order, so a
 * delayed match stays current and a stale never-closed slot before a finished one is skipped; else
 * the day's last match.
 */
export function courtNowPlaying(
  slots: CourtScheduleSlot[],
  courtId: string,
  dayKey: string,
  _now: number,
  timeZone: string,
): string | null {
  const day = courtDaySlots(slots, courtId, dayKey, timeZone);
  const live = day.find((s) => s.slot.inProgress);
  if (live) return live.slot.scheduleId;
  let anchor = -1;
  day.forEach((s, i) => {
    if (s.slot.completed) anchor = i;
  });
  const pick = day.slice(anchor + 1).find((s) => !s.slot.completed) ?? day[day.length - 1];
  return pick?.slot.scheduleId ?? null;
}

/** A public schedule's matches as court slots (the Watch page's mapping; a live match is in progress). */
export function scheduleSlots(
  matches: {
    id: string;
    courtId: string;
    scheduledAt: string;
    durationEstimateMin?: number | null;
    status: string;
  }[],
): CourtScheduleSlot[] {
  return matches.map((m) => ({
    scheduleId: m.id,
    courtId: m.courtId,
    startIso: m.scheduledAt,
    durationMin: m.durationEstimateMin ?? null,
    inProgress: m.status === 'live',
    completed: m.status === 'completed',
  }));
}

export type LiveSummary = { liveCount: number; headlineCourtId: string | null };

/**
 * Today's live feeds across `courtIds` (in court order): how many, and the court to lead with,
 * a court with a live app stream first, else the first court with any live feed.
 */
export function liveSummary(
  feeds: CourtFeed[],
  courtIds: string[],
  todayKey: string,
  slots: CourtScheduleSlot[],
  now: number,
  timeZone: string,
): LiveSummary {
  let liveCount = 0;
  let firstLive: string | null = null;
  let firstAppLive: string | null = null;
  for (const courtId of courtIds) {
    const live = feedsForCourtDay(feeds, courtId, todayKey).filter(
      (feed) => feedStatus(feed, todayKey, todayKey, slots, now, timeZone) === 'live',
    );
    if (!live.length) continue;
    liveCount += live.length;
    firstLive ??= courtId;
    if (live.some((feed) => feed.source === 'app')) firstAppLive ??= courtId;
  }
  return { liveCount, headlineCourtId: firstAppLive ?? firstLive };
}

/** A stored link as a feed; null when it no longer passes the URL rules (never shown). */
export function linkFeed(row: StreamLinkRow): CourtFeed | null {
  // Stored before a rule tightened: never shown.
  const recognised = recogniseStreamUrl(row.url);
  if (!recognised) return null;
  return {
    id: row.id,
    source: 'link',
    courtId: row.court_id,
    days: row.days,
    platform: recognised.platform,
    url: recognised.url,
    embedUrl: recognised.embedUrl,
    display: recognised.display,
    cameraName: row.camera_name,
    position: row.position,
    appState: null,
    alsoOn: [],
  };
}
